import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { CaretUp, CaretDown, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import DashboardFilters from "@/components/dashboard/DashboardFilters";
import MomentumTable from "@/components/dashboard/MomentumTable";
import SummaryCard from "@/components/dashboard/SummaryCard";

export default function Dashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("momentum_score");
  const [sortDir, setSortDir] = useState("desc");
  const [filter, setFilter] = useState("");
  const [caps, setCaps] = useState({ small: true, mid: true, large: true, mega: true });
  const [minVol, setMinVol] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [watchlist, setWatchlist] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const wanted = Object.entries(caps).filter(([, v]) => v).map(([k]) => k);
      if (wanted.length && wanted.length < 4) params.set("market_caps", wanted.join(","));
      if (minVol) params.set("min_volume", String(minVol));
      if (minPrice) params.set("min_price", String(minPrice));
      if (maxPrice) params.set("max_price", String(maxPrice));
      const u = await api.get("/me/universe");
      const userSyms = (u.data.symbols || []).join(",");
      if (userSyms) params.set("symbols", userSyms);
      const qs = params.toString();
      const [m, w] = await Promise.all([
        api.get(`/momentum${qs ? "?" + qs : ""}`),
        api.get("/watchlist"),
      ]);
      setRows(m.data.rows || []);
      setWatchlist(w.data.symbols || []);
    } catch {
      toast.error("Failed to load market data");
    } finally { setLoading(false); }
  }, [caps, minVol, minPrice, maxPrice]);

  useEffect(() => { load(); }, [load]);

  const sorted = [...rows]
    .filter((r) => !filter || r.symbol.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const av = a[sortKey]; const bv = b[sortKey];
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return ((av ?? 0) - (bv ?? 0)) * dir;
    });

  const toggleSort = (k) => {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };

  const toggleCap = (k) => setCaps((c) => ({ ...c, [k]: !c[k] }));
  const isInWatch = (s) => watchlist.includes(s);

  const toggleWatch = async (e, s) => {
    e.stopPropagation();
    try {
      const r = isInWatch(s)
        ? await api.delete(`/watchlist/${s}`)
        : await api.post(`/watchlist`, { symbol: s });
      setWatchlist(r.data.symbols);
      toast.success(`${s} ${isInWatch(s) ? "removed from" : "added to"} watchlist`);
    } catch { toast.error("Watchlist error"); }
  };

  const topGainers = [...rows].sort((a, b) => b.change_1d - a.change_1d).slice(0, 4);
  const topLosers = [...rows].sort((a, b) => a.change_1d - b.change_1d).slice(0, 4);
  const topMomentum = [...rows].sort((a, b) => b.momentum_score - a.momentum_score).slice(0, 4);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopBar />

      <div className="px-6 py-5">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="micro-label">/ momentum ranking</div>
            <h1 className="font-heading text-3xl mt-1">Optionable Universe</h1>
          </div>
          <DashboardFilters
            caps={caps} onToggleCap={toggleCap}
            minVol={minVol} onMinVolChange={setMinVol}
            minPrice={minPrice} onMinPriceChange={setMinPrice}
            maxPrice={maxPrice} onMaxPriceChange={setMaxPrice}
            filter={filter} onFilterChange={setFilter}
            loading={loading} onRefresh={load}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#222] border border-[#222] mb-6">
          <SummaryCard title="Top Momentum" icon={<Lightning size={14} weight="bold" className="text-[#ffcc00]" />} items={topMomentum} field="momentum_score" suffix="" />
          <SummaryCard title="Top Gainers" icon={<CaretUp size={14} weight="bold" className="text-[#00e676]" />} items={topGainers} field="change_1d" suffix="%" />
          <SummaryCard title="Top Losers" icon={<CaretDown size={14} weight="bold" className="text-[#ff3b30]" />} items={topLosers} field="change_1d" suffix="%" />
        </div>

        <MomentumTable
          rows={sorted}
          loading={loading}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={toggleSort}
          isInWatch={isInWatch}
          onWatchToggle={toggleWatch}
        />
      </div>
      <Footer />
    </div>
  );
}
